/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {
    Box,
    useMultiStyleConfig,
    Text,
    Button,
    Image,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    Drawer,
    DrawerBody,
    useBreakpointValue,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useShopperBasketsMutationHelper} from '@salesforce/commerce-sdk-react'
import {FormattedMessage, useIntl} from 'react-intl'
import useProduct from '../../../hooks/use-product'
import LoadingSpinner from '../../loading-spinner'
import {getUrl} from '../../../utils/cloudinary-utils'
import useTagManager from '../../../hooks/use-tag-manager'
import {useAddToCartDrawerContext} from '../../../hooks/use-add-to-cart-drawer'
import useMonetate from '../../../hooks/use-monetate'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useGlobaleContext} from '../../../contexts/globale'
import {getItemBadge, getPriceData, isLimitReached} from '../../../utils/product-utils'
import {useSiteContext} from '../../../contexts/site'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import ProductViewModal from '../../product-view-modal'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {logBasketErrorToSsr} from '../../../utils/log-basket-error-to-ssr'

const COOKIE_ID = 'gwp_promotion'

const QuickAdd = ({
    productId,
    variationValues,
    isHovered = false,
    isplp,
    variants,
    tilesTitle,
    category,
    wishlist,
    quickAddLabel = true,
    selectedVariant,
    tileProduct,
    selectedImage = '',
    basket,
    setShowAddtoCartSpinner,
    openQuickAddModal = false,
    analyticsProps = {},
    bonusDiscountLineItemId,
    pwpTargetPromotionId,
    index
}) => {
    const limitReached = isLimitReached(productId, basket)
    const {formatMessage} = useIntl()
    const {
        isOpen: isAddToCartDrawerOpen,
        onOpen: onAddToCartDrawerOpen,
        onClose: onAddToCartDrawerClose
    } = useAddToCartDrawerContext()
    const {
        isOpen: isQuickAddModalOpen,
        onOpen: onQuickAddModalOpen,
        onClose: onQuickAddModalClose
    } = useDisclosure()
    const tagManager = useTagManager()
    const monetate = useMonetate()
    const showToast = useToast()
    const showError = (message, statusCode) => {
        showToast({
            title: statusCode === '403' ? message : formatMessage(message),
            status: 'error'
        })
    }

    const {countryISO, currencyCode, globaleCurrency, setRefreshPrices} = useGlobaleContext()

    const {setOpenBonusModal} = useSiteContext()
    const {data: customer} = useCurrentCustomer()
    const location = useLocation()
    const colorAttributeName = variationValues
        ? Object.keys(variationValues).find((k) => k !== 'size')
        : ''
    const orderableVariant = variants?.some(
        (v) =>
            v?.orderable &&
            v?.variationValues?.[colorAttributeName] ===
                selectedVariant?.variationValues?.[colorAttributeName]
    )
    const [showSpinner, setShowSpinner] = useState(false)

    useEffect(() => {
        // Note: Please remove this once we move KS to PWA
        const resetStateOnBack = (event) => {
            if (event.persisted) {
                // Page was restored from bfcache (back-forward cache)
                setShowSpinner(false)
            }
        }
        window.addEventListener('pageshow', resetStateOnBack)
        return () => {
            window.removeEventListener('pageshow', resetStateOnBack)
        }
    }, [])
    const styles = useMultiStyleConfig('ProductTileStyles')
    const [fetchProduct, setFetchProduct] = useState(false)
    const [showAdded, setShowAdded] = useState(false)
    const isMobile = useBreakpointValue({base: true, lg: false})
    const [showAddtoCartSize, setShowAddtoCartSize] = useState(false)
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {data: product} = useProduct(
        {
            id: productId,
            c_selectedCountry: countryISO,
            perPricebook: true,
            c_selectedCurrency: currencyCode,
            expand: ['availability', 'promotions', 'images', 'prices', 'variations']
        },
        {
            keepPreviousData: true,
            enabled: fetchProduct
        }
    )

    useEffect(() => {
        if (isAddToCartDrawerOpen) {
            onAddToCartDrawerClose()
        }
    }, [location.pathname])

    const postAddToBagActions = async (variant) => {
        try {
            const productData = product?.product ?? tileProduct
            const analyticsName = isplp
                ? 'plp - ' + productData?.c_primaryCategory || productData?.primaryCategoryId
                : ''
            const filterSort =
                document
                    .querySelector('.product-list-sort select')
                    ?.selectedOptions?.[0]?.innerText?.trim() || undefined
            const listId = isplp ? 'plp' : analyticsProps?.component_id
            const itemsAdded = await addItemToNewOrExistingBasket([
                {
                    productId: variant?.type?.master
                        ? variant?.variants?.[0]?.productId
                        : variant?.productId || variant?.id,
                    price: variant?.type?.master ? variant?.variants?.[0]?.price : variant?.price,
                    ...(bonusDiscountLineItemId && {bonusDiscountLineItemId}),
                    quantity: 1, // hardcode the quantity on the quick add,
                    c_analyticsAttributes: JSON.stringify({
                        listID: listId || 'recommender',
                        listName: analyticsName || analyticsProps?.component_name || undefined,
                        itemSort: filterSort,
                        itemBadge: getItemBadge(productData, {selectedVariant: variant})
                    })
                }
            ])
            setRefreshPrices(true)
            setShowAdded(true)
            if (itemsAdded) {
                const promoId = (
                    itemsAdded?.bonusDiscountLineItems?.find((i) => /gwp/i?.test(i.promotionId)) ||
                    itemsAdded?.bonusDiscountLineItems?.[0]
                )?.promotionId // We'll give priority of thw GWP promotions as per the requirement.
                const cookie =
                    onClient() &&
                    new CookieStorage({
                        keySuffix: '',
                        sharedContext: !onClient()
                    })
                const gwpPromotionState = onClient() && cookie ? cookie.get(COOKIE_ID) : null
                if (product) {
                    var selectedSku = {
                        ...productData,
                        ...variant
                    }
                    const priceData = getPriceData(selectedSku)
                    if (!(promoId && gwpPromotionState !== 'closed') && !bonusDiscountLineItemId) {
                        onAddToCartDrawerOpen({
                            product: selectedSku,
                            itemsAdded,
                            selectedQuantity: 1,
                            priceData: priceData,
                            heroImage: selectedImage
                        })
                    }
                    if (!bonusDiscountLineItemId) {
                        setOpenBonusModal(true)
                    }
                    monetate.monetateTrackPage(
                        'addToBagModal',
                        window.mtBreadcrumb,
                        itemsAdded?.productItems,
                        customer?.customerType === 'registered',
                        wishlist?.customerProductListItems?.length || 0,
                        null,
                        null,
                        [
                            {
                                masterId:
                                    (tileProduct?.hitType === 'master'
                                        ? tileProduct?.productId
                                        : '') ||
                                    tileProduct?.master?.masterId ||
                                    tileProduct?.representedProduct?.c_masterVariantID ||
                                    tileProduct?.id,
                                variantId: variant?.type?.master
                                    ? variant?.variants?.[0]?.productId
                                    : variant?.productId || variant?.id
                            }
                        ],
                        itemsAdded?.giftCertificateItems
                    )
                }
            }
            tagManager.addToCart(
                tileProduct,
                selectedVariant,
                null,
                category,
                isplp ? 'plp' : 'recommender',
                null,
                false,
                {},
                false,
                false,
                false,
                analyticsProps
            )

            const addToCartData = {
                itemsAdded: [product ?? {product: productData}],
                method: isplp ? 'plp' : 'recommender',
                currency: globaleCurrency,
                indexPosition: index + 1,
                sorting: filterSort,
                listId: listId || 'recommender',
                listName: analyticsName || analyticsProps?.component_name
            }
            tagManager.addToCartGA4(addToCartData)
            setTimeout(() => {
                setShowAdded(false)
            }, 3000)
            onQuickAddModalClose()
        } catch (error) {
            const errResponse = error?.response ? await error.response.json() : null
            logger.error('ATCXL Error adding product to basket', {
                namespace: 'QuickAdd.handleAddToCart',
                additionalProperties: {
                    error,
                    errResponse,
                    productId: variant?.productId || variant?.id || productId,
                    basketId: basket?.basketId
                }
            })
            logBasketErrorToSsr('QuickAdd.handleAddToCart', {
                error,
                errResponse,
                productId: variant?.productId || variant?.id || productId,
                basketId: basket?.basketId
            })
            if (errResponse?.statusCode === '403') {
                showError(errResponse.statusMessage, errResponse.statusCode)
            } else {
                showError(API_ERROR_MESSAGE)
            }
            setRefreshPrices(true)
            setShowSpinner(false)
            setShowAddtoCartSize(false)
            setFetchProduct(false)
            throw error
        }
    }
    const handleAddToCart = async (sizeValue) => {
        const productData = product?.product ?? tileProduct
        const variant = colorAttributeName
            ? productData?.variants?.find(
                  (v) =>
                      v.variationValues?.size === sizeValue &&
                      v.variationValues?.[colorAttributeName] ===
                          variationValues?.[colorAttributeName]
              ) || tileProduct
            : productData
        setShowSpinner(true)
        if (
            productData?.c_preOrderSKUs?.length &&
            productData?.c_preOrderSKUs?.indexOf(variant?.productId) !== -1
        ) {
            const slugUrl = productData?.c_allPdpLinks?.find(
                (p) => p.id === variant?.productId
            )?.url
            if (slugUrl) {
                window.location.href = slugUrl
                return
            }
        }
        if (setShowAddtoCartSpinner) {
            setShowAddtoCartSpinner(true)
        }
        setShowAddtoCartSize(sizeValue)
        try {
            await postAddToBagActions(variant)
        } finally {
            setShowSpinner(false)
            setShowAddtoCartSize(false)
            setFetchProduct(false)
            if (setShowAddtoCartSpinner) {
                setShowAddtoCartSpinner(false)
            }
        }
    }

    const hasMoreThanOneOrderable = tileProduct?.variants?.filter((v) => v?.orderable)?.length > 1

    useEffect(() => {
        if (fetchProduct && product) {
            if (openQuickAddModal && hasMoreThanOneOrderable) {
                onQuickAddModalOpen(true)
                setFetchProduct(false)
                return
            }
            const isSizeAttrPresent = Object.keys(variationValues).find((k) => k === 'size')
            if (!isSizeAttrPresent) {
                handleAddToCart()
            }
        }
    }, [fetchProduct, product])

    useEffect(() => {
        if (product && product?.productId !== productId) {
            setFetchProduct(false)
        }
    }, [product?.productId, productId])

    useEffect(() => {
        // The mobile Drawer is rendered in a portal, outside the tile hover boundary.
        // Hover-leave cleanup only applies to the desktop inline size picker.
        if (isMobile === false && !isHovered && product) {
            setFetchProduct(false)
        }
    }, [isHovered, isMobile, product])

    const sizeSwatches = product?.product?.variationAttributes?.find(({id}) => id === 'size')
    const swatchGroupStyles = useMultiStyleConfig('SwatchGroup', {
        variant: 'square',
        disabled: false
    })
    const quickAddStyles = !quickAddLabel
        ? {opacity: 1, pointerEvents: 'auto', bottom: 'unset'}
        : {}

    useEffect(() => {
        if (isQuickAddModalOpen) {
            document.querySelector('html').style.overflow = 'hidden'
        } else {
            document.querySelector('html').style.overflow = ''
        }
    }, [isQuickAddModalOpen])
    return (
        <Box
            className="quick-add-wrapper"
            data-is-orderable={orderableVariant}
            {...styles.quickAdd.main}
            display={orderableVariant ? 'block' : 'none'}
            {...quickAddStyles}
        >
            {isplp && showSpinner && <LoadingSpinner position="fixed" top="auto" />}
            {quickAddLabel && (
                <Button
                    className="quick-add-button"
                    display={{
                        base: limitReached ? 'none' : 'flex',
                        lg: limitReached || (fetchProduct && product) ? 'none' : 'flex'
                    }}
                    isLoading={fetchProduct && !product}
                    onClick={(e) => {
                        e.preventDefault()
                        window.setTimeout(() => {
                            setFetchProduct(true)
                        }, 100)
                    }}
                    {...styles.quickAdd.button}
                >
                    {showAdded ? (
                        <FormattedMessage defaultMessage="Added" id="added.to.cart" />
                    ) : (
                        <FormattedMessage defaultMessage="Quick Add" id="quick.add" />
                    )}
                </Button>
            )}
            {!quickAddLabel && (
                <Button
                    display="flex"
                    isLoading={(fetchProduct && !product) || showSpinner}
                    className="add-only-btn"
                    width={!quickAddLabel ? '100%' : 'unset'}
                    height={!quickAddLabel ? '26px' : 'unset'}
                    onClick={() => {
                        if (openQuickAddModal && hasMoreThanOneOrderable) {
                            window.listId = tilesTitle || 'recommender'
                            onQuickAddModalOpen(true)
                        } else {
                            handleAddToCart()
                        }
                    }}
                    {...styles.quickAdd.button}
                >
                    {showAdded ? (
                        <FormattedMessage defaultMessage="Added" id="added.to.cart" />
                    ) : (
                        <FormattedMessage defaultMessage="Add" id="add.to.cart" />
                    )}
                </Button>
            )}

            {isQuickAddModalOpen && (
                <ProductViewModal
                    isOpen={isQuickAddModalOpen}
                    onOpen={onQuickAddModalOpen}
                    onClose={onQuickAddModalClose}
                    isQuickAddModal={openQuickAddModal}
                    product={tileProduct}
                    pwpTargetPromotionId={pwpTargetPromotionId}
                    updateCart={async (variant) => {
                        setShowSpinner(true)
                        try {
                            await postAddToBagActions(variant)
                        } finally {
                            setShowSpinner(false)
                        }
                    }}
                    showDeliveryOptions={false}
                />
            )}
            {fetchProduct && sizeSwatches?.values ? (
                <Box
                    className={
                        'sizes-wrapper ' +
                        (fetchProduct && sizeSwatches?.values?.length
                            ? 'sizes-available'
                            : 'no-size-available')
                    }
                    opacity={fetchProduct && sizeSwatches?.values?.length ? '0' : '-1'}
                    padding={fetchProduct && sizeSwatches?.values?.length ? '15px' : 0}
                    height={fetchProduct && sizeSwatches?.values?.length ? 'fit-content' : '0'}
                    pointerEvents={fetchProduct && sizeSwatches?.values?.length ? 'auto' : 'none'}
                    {...styles.quickAdd.sizesWrapper}
                >
                    {fetchProduct &&
                        sizeSwatches?.values?.map?.((item, index) => {
                            const {name, orderable, value} = item
                            return (
                                <Button
                                    {...swatchGroupStyles.swatch}
                                    key={index}
                                    className={orderable ? 'size-swatch' : 'disabled-swatch'}
                                    backgroundColor={orderable ? 'gray.400' : 'gray.100'}
                                    color={orderable ? 'black.100' : 'gray.600'}
                                    borderRadius="3px"
                                    pointerEvents={orderable ? 'auto' : 'none'}
                                    isLoading={showAddtoCartSize === value}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        handleAddToCart(value)
                                    }}
                                    data-value={value}
                                    data-orderable={orderable}
                                    {...styles.quickAdd.sizeSwatch}
                                >
                                    {name}
                                </Button>
                            )
                        })}
                </Box>
            ) : null}
            <Drawer
                placement="bottom"
                isOpen={fetchProduct && sizeSwatches?.values?.length && isMobile}
                onClose={() => {
                    setFetchProduct(false)
                }}
                blockScrollOnMount
                size="sm"
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                className="quick-add-drawer"
                isFullHeight={false}
                // Add the following 1 line to fix the Safari behavior of Drawer Close
                returnFocusOnClose={false}
            >
                <DrawerOverlay
                    background="blackAlpha600"
                    height="100%"
                    className="quick-add-drawer-overlay"
                />
                <DrawerContent
                    {...styles.quickAdd.drawerContent}
                    className="quick-add-drawer-content"
                >
                    <DrawerHeader
                        {...styles.quickAdd.drawerHeader}
                        className="quick-add-drawer-header"
                    >
                        <Text
                            color="black.100"
                            fontSize="16px"
                            fontWeight="400"
                            className="quick-add-drawer-text"
                        >
                            <FormattedMessage defaultMessage="Size" id="quick.add.size" />
                        </Text>
                        <Text
                            color="black.100"
                            fontSize="16px"
                            marginLeft="15px"
                            fontWeight="300"
                            className="quick-add-drawer-select-size"
                        >
                            <FormattedMessage
                                defaultMessage="Select a Size"
                                id="quick.add.select.a.size"
                            />
                        </Text>
                    </DrawerHeader>
                    <Box
                        position="absolute"
                        top="10px"
                        right="10px"
                        className="quick-add-drawer-box"
                        borderRadius="50%"
                        _hover={{
                            backgroundColor: 'gray.100'
                        }}
                        padding="15px"
                        transition="all, 0.3s ease"
                        cursor="pointer"
                        onClick={() => {
                            setFetchProduct(false)
                        }}
                    >
                        <Image
                            width="15px"
                            className="quick-add-drawer-image"
                            height="15px"
                            alt="Cross Icon"
                            src={getUrl('UIImages/images/crossed-icon.svg')}
                        />
                    </Box>
                    <DrawerBody
                        padding={0}
                        className="modal-filters"
                        display="inline-flex"
                        marginBottom="15px"
                        flexWrap="wrap"
                        gap="10px"
                    >
                        {sizeSwatches?.values?.map?.((item, index) => {
                            const {name, orderable, value} = item
                            return (
                                <Button
                                    {...swatchGroupStyles.swatch}
                                    key={index}
                                    className={orderable ? 'size-swatch' : 'disabled-swatch'}
                                    backgroundColor={orderable ? 'gray.400' : 'gray.100'}
                                    color={orderable ? 'black.100' : 'gray.600'}
                                    pointerEvents={orderable ? 'auto' : 'none'}
                                    borderRadius="3px"
                                    isLoading={showAddtoCartSize === value}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        handleAddToCart(value)
                                    }}
                                    data-value={value}
                                    data-orderable={orderable}
                                    {...styles.quickAdd.sizeSwatch}
                                >
                                    {name}
                                </Button>
                            )
                        })}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    )
}

QuickAdd.propTypes = {
    variationValues: PropTypes.object,
    basket: PropTypes.object,
    productId: PropTypes.string,
    isplp: PropTypes.bool,
    quickAddLabel: PropTypes.bool,
    setShowAddtoCartSpinner: PropTypes.func,
    isHovered: PropTypes.bool,
    selectedVariant: PropTypes.any,
    variants: PropTypes.any,
    wishlist: PropTypes.any,
    category: PropTypes.any,
    selectedImage: PropTypes.any,
    tileProduct: PropTypes.any,
    openQuickAddModal: PropTypes.bool,
    analyticsProps: PropTypes.object,
    bonusDiscountLineItemId: PropTypes.string,
    pwpTargetPromotionId: PropTypes.string,
    tilesTitle: PropTypes.any,
    index: PropTypes.any
}

export default QuickAdd
